import { useCallback, useEffect, useRef, useState } from "react";

/**
 * Manages toast mount, visibility duration, and exit animation lifecycle.
 *
 * @param {boolean} isToast
 * @param {number} duration
 * @param {Function} onToastChange
 * @returns {{ rendered: boolean, phase: "enter"|"visible"|"exit", dismiss: Function, handleTransitionEnd: Function }}
 */
const useToastAnimation = (isToast, duration, onToastChange) => {
  const [rendered, setRendered] = useState(false);
  const [phase, setPhase] = useState(/** @type {"enter"|"visible"|"exit"} */ ("enter"));
  const dismissTimeoutRef = useRef(/** @type {ReturnType<typeof setTimeout>|null} */ (null));
  const enterFrameRef = useRef(/** @type {number|null} */ (null));
  const isExitingRef = useRef(false);
  const renderedRef = useRef(false);

  const clearDismissTimeout = useCallback(() => {
    if (dismissTimeoutRef.current != null) {
      clearTimeout(dismissTimeoutRef.current);
      dismissTimeoutRef.current = null;
    }
  }, []);

  const dismiss = useCallback(() => {
    if (isExitingRef.current) {
      return;
    }
    isExitingRef.current = true;
    clearDismissTimeout();
    setPhase("exit");
  }, [clearDismissTimeout]);

  useEffect(() => {
    if (isToast) {
      isExitingRef.current = false;
      renderedRef.current = true;
      setRendered(true);
      setPhase("enter");

      enterFrameRef.current = requestAnimationFrame(() => {
        enterFrameRef.current = requestAnimationFrame(() => {
          setPhase("visible");
        });
      });

      dismissTimeoutRef.current = setTimeout(dismiss, duration);

      return () => {
        if (enterFrameRef.current != null) {
          cancelAnimationFrame(enterFrameRef.current);
          enterFrameRef.current = null;
        }
        clearDismissTimeout();
      };
    }

    if (renderedRef.current && !isExitingRef.current) {
      dismiss();
    }

    return undefined;
  }, [isToast, duration, dismiss, clearDismissTimeout]);

  const handleTransitionEnd = useCallback(
    (event) => {
      if (event.propertyName !== "transform" || phase !== "exit") {
        return;
      }

      isExitingRef.current = false;
      renderedRef.current = false;
      onToastChange(false);
      setRendered(false);
      setPhase("enter");
    },
    [onToastChange, phase],
  );

  return {
    rendered,
    phase,
    dismiss,
    handleTransitionEnd,
  };
};

export default useToastAnimation;
